import React, { useCallback } from "react";
import { FaFire } from "react-icons/fa";
import PostCard from "../profile/PostCard";
import api from "../../utils/api/axiosInstance";
import { filterPostsForFlexFeed } from "../../utils/feedPostTypeFilters";
import { useFeedDedicatedTab } from "../../hooks/useFeedDedicatedTab";
import FeedTabErrorState from "./FeedTabErrorState";
import styles from "../../styles/pages/feed/FeedPage.module.css";

const FeedFlexTab = ({
  active,
  activeFilter = "forYou",
  onCreateFlexPost,
  onOpenPost,
  onPostDeleted,
  onLikeUpdate,
  onSaveUpdate,
  onCommentUpdate,
  onVoteUpdate,
  onPostUpdated,
  onEditPost,
  onCommentOpen,
  activeDecision = null,
  shopperCategoryKeys = null,
  scrollRoot = null,
}) => {
  const fetchPage = useCallback(async (pageNum) => {
    const res = await api.get("/posts/flex", {
      params: { page: pageNum, limit: 10, filter: activeFilter },
    });
    return {
      items: filterPostsForFlexFeed(res.data.posts || []),
      hasMore: res.data.pagination?.hasMore ?? false,
    };
  }, [activeFilter]);

  const {
    items: flexPosts,
    setItems: setFlexPosts,
    loading,
    error,
    hasMore,
    loadMoreRef,
    retry,
  } = useFeedDedicatedTab({
    active,
    fetchPage,
    errorLabel: "flex posts",
    scrollRoot,
  });

  const handlePostDeleted = useCallback(
    (postId) => {
      setFlexPosts((prev) => prev.filter((p) => String(p._id) !== String(postId)));
      onPostDeleted?.(postId);
    },
    [onPostDeleted, setFlexPosts],
  );

  const handlePostUpdated = useCallback(
    (updatedPost) => {
      setFlexPosts((prev) =>
        prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)),
      );
      onPostUpdated?.(updatedPost);
    },
    [onPostUpdated, setFlexPosts],
  );

  if (!active) return null;

  if (loading && flexPosts.length === 0) {
    return (
      <div className={styles.skeletonList} aria-hidden="true">
        {[0, 1, 2].map((idx) => (
          <div key={idx} className={styles.skeletonCard}>
            <div className={styles.skeletonHeader}>
              <span className={styles.skeletonAvatar} />
              <div className={styles.skeletonHeaderText}>
                <span className={styles.skeletonLineShort} />
                <span className={styles.skeletonLineTiny} />
              </div>
            </div>
            <div className={styles.skeletonImage} />
            <div className={styles.skeletonContent}>
              <span className={styles.skeletonLineMedium} />
              <span className={styles.skeletonLineFull} />
              <span className={styles.skeletonLineMedium} />
            </div>
            <div className={styles.skeletonActions}>
              <span className={styles.skeletonAction} />
              <span className={styles.skeletonAction} />
              <span className={styles.skeletonAction} />
              <span className={styles.skeletonAction} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error && flexPosts.length === 0) {
    return (
      <FeedTabErrorState
        title="Could not load flex posts"
        message={error}
        onRetry={retry}
      />
    );
  }

  if (flexPosts.length === 0) {
    const isFollowing = activeFilter === "following";
    return (
      <div className={styles.emptyState}>
        <FaFire className={styles.emptyIcon} />
        <h2>
          {isFollowing
            ? "No flex posts from people you follow"
            : "Nothing to flex yet"}
        </h2>
        <p>
          {isFollowing
            ? "Follow more people to see their setups and gear here."
            : "Share a post with a product tag to show up here!"}
        </p>
        <button
          type="button"
          className={styles.exploreButton}
          onClick={onCreateFlexPost}
        >
          Create a Flex Post
        </button>
      </div>
    );
  }

  return (
    <div className={styles.postsContainer}>
      {flexPosts.map((post) => (
        <div
          key={post._id}
          role="button"
          tabIndex={0}
          className={styles.postClickableWrapper}
          onClick={(event) => onOpenPost?.(post, event)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            const tag = e.target?.tagName;
            if (
              tag === "INPUT" ||
              tag === "TEXTAREA" ||
              tag === "SELECT" ||
              e.target?.isContentEditable
            ) {
              return;
            }
            e.preventDefault();
            onOpenPost?.(post, e);
          }}
        >
          <PostCard
            post={post}
            activeDecision={activeDecision}
            shopperCategoryKeys={shopperCategoryKeys}
            onDelete={handlePostDeleted}
            onLikeUpdate={onLikeUpdate}
            onSaveUpdate={onSaveUpdate}
            onCommentUpdate={onCommentUpdate}
            onVoteUpdate={onVoteUpdate}
            onPostUpdated={handlePostUpdated}
            onEdit={() => onEditPost?.(post)}
            commentAction="modal"
            onCommentNavigate={() => onCommentOpen?.(post)}
            disableCardClick={true}
          />
        </div>
      ))}
      {hasMore && (
        <div ref={loadMoreRef} className={styles.loadMoreTrigger} />
      )}
    </div>
  );
};

export default React.memo(FeedFlexTab);
